import React, { useState, useMemo, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'

import { OWNER_STATUSES, CERTIFICATE_STATUSES } from 'constants/statuses'

import { date, TRANSFER_PERMISSION_TOOLTIP } from 'utils'

import type { CertificateAdmin, Customer } from 'types'
import type { Owner } from 'store/owners/types'

import { useDidMountEffect, useCanAccess, useReferrerState } from 'hooks'
import { useOwner } from 'hooks/api'
import { BadgePlusIcon, TrashIcon, PencilIcon, InfoIcon, ArrowLeftRightIcon, ArchiveIcon, ArchiveRestoreIcon } from 'lucide-react'
import { ROUTES } from 'routes'

import { Stack, Tooltip } from '@mui/material'

import { Button, Alert } from '@repo/ui'
import {
  Header,
  DeleteOwnerModal,
  ArchiveOwnerModal,
  TransferCertificateModal,
  ActionDropdown,
  EmptyState,
  InfoBlock,
  InfoRow,
  InfoItem,
  Table,
  StatusChipCell,
  CertificateCell,
  type ActionDropdownItem,
} from 'components'
import { ExampleKindBadge } from 'components/display/ExampleKindBadge'
import { OwnerPageSkeleton } from './OwnerPageSkeleton'

type Filters = {
  limit: number
  offset: number
  search: string | null
}

const DEFAULT_FILTERS: Filters = {
  limit: 10,
  offset: 0,
  search: null,
}

const ownerToCustomer = (owner: Owner): Customer => {
  // VERA-914: the owner-detail response may omit the combined `name` (it carries
  // first_name/last_name) — guard against `.split` on undefined (was crashing the
  // page for owners with no `name`, e.g. reached from the People table).
  const fullName = owner.name || [owner.first_name, owner.last_name].filter(Boolean).join(' ') || ''
  const nameParts = fullName.split(' ')
  return {
    id: owner.id,
    first_name: nameParts[0] || '',
    last_name: nameParts.slice(1).join(' ') || '',
    username: null,
    birthday: null,
    avatar: owner.avatar,
    phone: owner.phone,
    email: owner.email,
    gender: owner.gender,
    active: owner.status === 'active',
    created_at: owner.registrationDate,
    updated_at: owner.registrationDate,
    scans_count: owner.scans,
    certificates_count: owner.certificates,
    // VRG-416 — read the REAL directional counts off the detail response. The
    // owner-detail endpoint already computes them as two COUNT subqueries
    // (`transfers.to_owner_id` / `transfers.from_owner_id`) and returns
    // transfers_in_count / transfers_out_count / transfers_count.
    //
    // These used to be `Math.floor(owner.transfers / 2)` / `Math.ceil(...)` —
    // one real number halved to manufacture two. That was doubly broken here:
    // the detail response has no `transfers` key at all, so it evaluated to
    // NaN. Pass through whatever the API actually returned, undefined included.
    transfers_in_count: owner.transfers_in_count,
    transfers_out_count: owner.transfers_out_count,
    transfers_count: owner.transfers_count,
  }
}

export const OwnerPage = () => {
  const { id } = useParams()
  const [filters, setFilters] = useState<Filters>(DEFAULT_FILTERS)
  const navigate = useNavigate()
  const [openDeleteModal, setOpenDeleteModal] = useState(false)
  const [openArchiveModal, setOpenArchiveModal] = useState(false)
  const [isUnarchiving, setIsUnarchiving] = useState(false)
  const [openTransferModal, setOpenTransferModal] = useState(false)
  const { isHasAccess: isCanTransfer } = useCanAccess({ action: 'transfer_certificate' })
  // Detail → detail: the derived label would be the Owners list, not this owner.
  const referrer = useReferrerState('Owner')

  const { data, isLoading, refetch } = useOwner({
    ownerId: id as string,
    offset: filters.offset,
    limit: filters.limit,
  })

  const certificates = data?.certificates?.data || []
  const total = data?.certificates?.total || 0
  const owner = data?.owner

  // `||`, NOT `??` (VERA-304): the server sends `''` for an owner with no name, and
  // `??` does not fall through on an empty string — it would title the page with a
  // blank. The email is the heading for a nameless owner; never a placeholder word.
  const ownerName = owner?.name || owner?.email || '—'
  const ownerNameFirstLetter = ownerName?.charAt(0)?.toLocaleUpperCase()

  const ownerStatus = owner?.archived ? 'archived' : (owner?.status ?? 'pending')

  const columns = useMemo(
    () => [
      {
        field: 'certificate',
        headerName: 'Certificate',
        pinned: 'left',
        width: 300,
        type: 'string',
        renderCell: ({ row }) => <CertificateCell certificate={row} />,
      },
      {
        field: 'date_of_ownership',
        headerName: 'Claimed ownership',
        type: 'dateTime',
        valueGetter: (value) => value && new Date(value),
        width: 220,
      },
      {
        field: 'status',
        headerName: 'Status',
        type: 'string',
        width: 150,
        renderCell: ({ value }) => <StatusChipCell statuses={CERTIFICATE_STATUSES} status={value} />,
      },
      {
        field: 'scan_counter',
        headerName: 'Scans',
        type: 'number',
        width: 120,
      },
      {
        field: 'share_counter',
        headerName: 'Shares',
        type: 'number',
        width: 120,
      },
      {
        field: 'actions',
        headerName: false,
        sortable: false,
        align: 'center',
        width: 54,
        pinned: 'right',
        renderCell: ({ row }) => <ActionDropdown items={getActionItems(row)} />,
      },
    ],
    [ownerName]
  )

  useDidMountEffect(() => {
    refetch()
  }, [filters.offset, filters.limit])

  const handleViewCertificate = (row: CertificateAdmin) => {
    navigate(ROUTES.CERTIFICATE(row.id), { state: referrer })
  }

  const handleOwnerDeleted = useCallback(() => {
    navigate(ROUTES.OWNERS)
  }, [navigate])

  const handleOpenArchiveModal = (unarchiving = false) => {
    setIsUnarchiving(unarchiving)
    setOpenArchiveModal(true)
  }

  const getActionItems = useCallback(
    (row: CertificateAdmin): ActionDropdownItem[] => [
      {
        label: 'Details',
        onClick: () => handleViewCertificate(row),
        icon: <InfoIcon strokeWidth={1} />,
      },
    ],
    [referrer]
  )

  if (!isLoading && !owner) {
    return (
      <Stack spacing={4} alignItems="center" sx={{ py: 12 }}>
        <Alert
          severity="error"
          title="Owner not found"
          description="The owner you're looking for doesn't exist or has been removed."
        />
        <Button variant="secondary" onClick={() => navigate(ROUTES.OWNERS)}>
          Back to Owners
        </Button>
      </Stack>
    )
  }

  // Footprint-parity placeholder (§19). It takes `columns` so it can render the REAL table in its
  // loading state rather than a box of a guessed height — see OwnerPageSkeleton's docstring for the
  // measurements and for what the previous branch got wrong.
  if (isLoading) {
    return <OwnerPageSkeleton columns={columns} />
  }

  return (
    <>
      <Header
        avatar={{ url: owner?.avatar || undefined, letters: ownerNameFirstLetter }}
        title={ownerName}
        // VRG-361 — a seeded owner reads as a real person otherwise; there is nothing
        // else on this page that would say so.
        titleAdornment={<ExampleKindBadge kind={owner?.example_kind} />}
        status={{
          statuses: OWNER_STATUSES,
          value: ownerStatus,
        }}
        breadcrumbsOveride={ownerName}
        rightComponents={
          <Stack direction="row" spacing="8px" alignItems="center">
            {owner?.archived ? (
              <Button
                variant="secondary"
                onClick={() => handleOpenArchiveModal(true)}
                startIcon={<ArchiveRestoreIcon strokeWidth={1} />}
              >
                Unarchive
              </Button>
            ) : (
              <Button
                variant="secondary"
                onClick={() => handleOpenArchiveModal(false)}
                startIcon={<ArchiveIcon strokeWidth={1} />}
              >
                Archive
              </Button>
            )}
            <Button
              variant="secondary"
              onClick={() => owner && navigate(ROUTES.OWNER_EDIT(owner.id))}
              startIcon={<PencilIcon strokeWidth={1} />}
            >
              Edit
            </Button>
            <Button
              variant="secondary"
              destructive="hover"
              onClick={() => setOpenDeleteModal(true)}
              startIcon={<TrashIcon strokeWidth={1} />}
            >
              Delete
            </Button>
            {/* §46 (working-conventions): permission disables, record state HIDES.
                This previously carried both reasons in one `disabled`, and the comment on it
                conceded the tell — "no tooltip copy for the archived-state disable, that's a
                state fact". §46's forcing function reads that the other way round: if you
                cannot write the sentence explaining a disable, it is not a permission block,
                it is record state, and it belongs in `visible`. So the archived case now
                hides, and the obligation hiding creates is satisfied one button to the left —
                Unarchive is the action that resolves it.

                Permission keeps the visible+disabled+tooltip treatment: an admin elsewhere
                owns that resolution, so the control is the only channel that tells this user
                the capability exists. A disabled MUI Button swallows pointer events, so the
                Tooltip needs the <span> wrapper to have a hover target (same pattern as
                "View live" on CertificatePage). */}
            {!owner?.archived && (
              <Tooltip
                title={
                  !isCanTransfer
                    ? TRANSFER_PERMISSION_TOOLTIP
                    : ''
                }
              >
                <span>
                  <Button
                    onClick={() => setOpenTransferModal(true)}
                    disabled={!isCanTransfer}
                    startIcon={<ArrowLeftRightIcon strokeWidth={1} />}
                  >
                    Transfer
                  </Button>
                </span>
              </Tooltip>
            )}
          </Stack>
        }
      />

      <InfoBlock sx={{ mb: 4 }}>
        <InfoRow>
          <InfoItem label="Registration date" value={owner?.registrationDate ? date(owner.registrationDate) : '—'} />
          <InfoItem label="Email" value={owner?.email || '—'} />
          <InfoItem label="Phone" value={owner?.phone || '—'} />
          <InfoItem label="Age" value={owner?.age ?? '—'} />
          <InfoItem label="Gender" value={owner?.gender || '—'} />
        </InfoRow>
      </InfoBlock>

      <Table
        dataTour="owner-detail"
        loading={isLoading}
        fillBottom
        columns={columns}
        rows={certificates}
        showSelection={false}
        emptyState={
          <EmptyState
            title="No certificates"
            text="Use the Transfer action above to transfer certificates to this owner"
            icon={<BadgePlusIcon strokeWidth={1} size={32} />}
          />
        }
      />

      <DeleteOwnerModal
        owner={owner}
        open={openDeleteModal}
        onClose={() => setOpenDeleteModal(false)}
        refetch={refetch}
        onDeleted={handleOwnerDeleted}
      />
      <ArchiveOwnerModal
        owner={owner ?? null}
        open={openArchiveModal}
        onClose={() => {
          setOpenArchiveModal(false)
          setIsUnarchiving(false)
        }}
        refetch={refetch}
        unarchiving={isUnarchiving}
      />
      <TransferCertificateModal
        open={openTransferModal}
        owner={owner ? ownerToCustomer(owner) : null}
        onClose={() => setOpenTransferModal(false)}
        refetch={refetch}
      />
    </>
  )
}
